import React, { useCallback, useEffect, useMemo } from 'react'
import { LivestockClass, LivestockBalanceItem, LivestockClassType } from 'api/models'
import { Form, Table } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { RegisterFormFn, useGhgCalculatorFormContext } from './use-ghg-calculator-form'
import { FormValuesWatcher } from './FormValuesWatcher'
import { GhgCalculatorNavigation } from './GhgCalculatorNavigation'
import { NumberField } from './base/form/NumberField'
import * as yup from 'yup'
import { useYupValidationResolver } from 'hooks/use-yup-validation-resolver'
import sumBy from 'lodash/sumBy'
import { coerceToNumber, formatNumber } from 'utils/formatting'
import { yupNullableMinZeroInteger } from 'utils/validation'

import { Alert } from 'react-bootstrap'
import { LivestockClassTooltip } from './base/LivestockClassTooltip'

interface FormValues {
  items: Array<LivestockBalanceItem>
}

const validationSchema = yup.object({
  items: yup.array().of(
    yup.object({
      owned_on_at_open: yupNullableMinZeroInteger(),
      owned_on_at_close: yupNullableMinZeroInteger(),
      owned_off_at_open: yupNullableMinZeroInteger(),
      owned_off_at_close: yupNullableMinZeroInteger(),
      grazed_on_at_open: yupNullableMinZeroInteger(),
      grazed_on_at_close: yupNullableMinZeroInteger(),
    })
  ),
})

const fieldNames: Array<keyof LivestockBalanceItem> = [
  'owned_on_at_open',
  'owned_on_at_close',
  'owned_off_at_open',
  'owned_off_at_close',
  'grazed_on_at_open',
  'grazed_on_at_close',
]

const fieldLabels: { [K in keyof LivestockBalanceItem]?: string } = {
  owned_on_at_open: 'Owned on-farm (head) at open',
  owned_on_at_close: 'Owned on-farm (head) at close',
  owned_off_at_open: 'Owned off-farm (head) at open',
  owned_off_at_close: 'Owned off-farm (head) at close',
  grazed_on_at_open: 'Grazed on-farm (head) at open',
  grazed_on_at_close: 'Grazed on-farm (head) at close',
}

export const GhgLivestockBalancesStep = () => {
  const {
    livestockClasses,
    livestockClassTypes,
    livestockBalances,
    setLivestockBalances,
    registerForm,
  } = useGhgCalculatorFormContext()
  if (!(livestockBalances && livestockClasses && livestockClassTypes)) {
    return <div>Loading livestock classes...</div>
  }
  return (
    <GhgLivestockBalancesStepInner
      livestockClasses={livestockClasses}
      livestockClassTypes={livestockClassTypes}
      livestockBalances={livestockBalances}
      setLivestockBalances={setLivestockBalances}
      registerForm={registerForm}
    />
  )
}

const GhgLivestockBalancesStepInner = (props: {
  livestockClasses: LivestockClass[]
  livestockBalances: LivestockBalanceItem[]
  livestockClassTypes: LivestockClassType[]
  setLivestockBalances: (items: LivestockBalanceItem[]) => void
  registerForm: RegisterFormFn
}) => {
  const {
    livestockClasses,
    livestockClassTypes,
    livestockBalances,
    setLivestockBalances,
    registerForm,
  } = props
  const resolver = useYupValidationResolver(validationSchema)

  const formBag = useForm<FormValues>({
    mode: 'all',
    defaultValues: {
      items: livestockBalances,
    },
    shouldUnregister: false,
    resolver,
  })

  const { control, handleSubmit, watch, formState } = formBag

  useEffect(() => {
    registerForm(formBag)
    return () => registerForm(null)
  }, [formBag, registerForm])

  // formState is wrapped with Proxy to improve render performance, so make sure you invoke or read it
  // before render in order to enable the state update.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { errors, touched } = formState

  const saveLivestockBalances = useCallback(
    ({ items }: { items: LivestockBalanceItem[] }) => {
      setLivestockBalances(items)
    },
    [setLivestockBalances]
  )

  // TODO: This means the whole form has to re-render at every keystroke. Watch
  // out for performance issues.
  const items = watch('items')

  //
  // totalRows for each liveStockClassType
  //
  const totalRows = useMemo(() => {
    return livestockClassTypes.reduce((acc, livestockType) => {
      const balanceItems = items.filter((balanceItem) => {
        const stockClass = livestockClasses.find((clz) => clz.id === balanceItem.classification)

        if (!stockClass || stockClass.livestock_type !== livestockType.id) {
          return false
        }

        return true
      })

      acc[livestockType.name] = [
        ...fieldNames.map((fieldName) => (
          <td className="number bg-light col-pair">
            {formatNumber(
              sumBy(balanceItems, (item) => coerceToNumber(item[fieldName])),
              0
            )}
          </td>
        )),
      ]

      return acc
    }, {} as Record<string, React.ReactElement[]>)
  }, [livestockClassTypes, livestockClasses, items])

  return (
    <Form>
      {formState.isSubmitted && Object.keys(errors).length > 0 && (
        <div className="mt-4 mb-4">
          <span className="text-danger">Please correct the errors below to continue.</span>
        </div>
      )}
      <Alert variant="info">
        In this tab, all animals must be aged up a livestock class at the season’s ‘close’ – if
        you’re using opening and closing numbers from financial statements this should already be
        done, but if you’re not using financial statements you’ll need to age up animals, e.g.
        retained lambs become hoggets at close, Heifers R2 become MA Cows.
      </Alert>
      {livestockClassTypes.map((livestockType, livestockTypeIdx) => (
        <fieldset key={livestockType.id}>
          <legend className="section-heading">{livestockType.name_display}</legend>
          {livestockType.name === 'DAIRY' && (
            <Alert variant="info">
              Represents owned and non-owned (grazing) Dairy cattle that are not currently being
              milked.
            </Alert>
          )}
          <Table
            key={livestockType.id}
            data-testid={`${livestockType.name}-table`}
            role="grid"
            className="table-custom table-responsive"
          >
            <BalanceTableThead hidden={false} />
            <tbody>
              {livestockClasses
                .filter((stockClass) => stockClass.livestock_type === livestockType.id)
                .map((stockClass, stockClassIdx) => {
                  const balanceItemIdx = items.findIndex(
                    (balance) => balance.classification === stockClass.id
                  )

                  if (balanceItemIdx === -1) {
                    return null
                  }

                  const prefix = `items[${balanceItemIdx}]`

                  return (
                    <tr key={stockClass.id}>
                      <th scope="row" className="w-10">
                        <LivestockClassTooltip
                          livestockType={livestockType.name}
                          livestockClassName={stockClass.name}
                        />
                      </th>

                      {fieldNames.map((fieldName, fieldIdx) => (
                        <td className="number bg-light col-pair w-10" key={fieldName}>
                          <Form.Label htmlFor={`${prefix}.${fieldName}`} className="sr-only">
                            {fieldLabels[fieldName]}
                          </Form.Label>
                          <NumberField
                            id={`${prefix}.${fieldName}`}
                            name={`${prefix}.${fieldName}`}
                            control={control}
                            placeholder="0"
                            data-testid={`${stockClass.name}.${fieldName}`}
                            autoFocus={
                              livestockTypeIdx === 0 && stockClassIdx === 0 && fieldIdx === 0
                            }
                            maxLength={6}
                          />
                        </td>
                      ))}
                    </tr>
                  )
                })}
            </tbody>
            <tfoot>
              <tr>
                <td scope="row" className="text-uppercase">
                  Total
                </td>
                {totalRows[livestockType.name]?.map((content, idx) => (
                  <React.Fragment key={idx}>{content}</React.Fragment>
                ))}
              </tr>
            </tfoot>
          </Table>
        </fieldset>
      ))}

      <FormValuesWatcher control={control} onValuesChange={saveLivestockBalances} />
      {Object.keys(errors).length > 0 && (
        <p className="text-right">
          <span className="text-danger">Please correct the errors above to continue.</span>
        </p>
      )}
      <GhgCalculatorNavigation handleSubmit={handleSubmit} />
    </Form>
  )
}

function BalanceTableThead(props: { hidden: boolean }) {
  return (
    <>
      <colgroup>
        <col />
        <col />
        <col span={2} />
        <col span={2} />
        <col span={2} />
        <col />
        <col />
      </colgroup>
      <thead className={props.hidden ? 'sr-only' : ''}>
        <tr>
          <th scope="col" rowSpan={2}>
            Stock
          </th>
          <th scope="col" colSpan={2} className="text-center">
            Owned on-farm (Head)
          </th>
          <th scope="col" colSpan={2} className="text-center">
            Owned off-farm (Head)
          </th>
          <th scope="col" colSpan={2} className="text-center">
            Grazed on-farm (Head)
          </th>
        </tr>
        <tr>
          <th scope="col" className="text-center col-pair">
            At Open
          </th>
          <th scope="col" className="text-center col-pair">
            At Close
          </th>
          <th scope="col" className="text-center col-pair">
            At Open
          </th>
          <th scope="col" className="text-center col-pair">
            At Close
          </th>
          <th scope="col" className="text-center col-pair">
            At Open
          </th>
          <th scope="col" className="text-center col-pair">
            At Close
          </th>
        </tr>
      </thead>
    </>
  )
}
